import { useCallback } from 'react'

import { useCreateDaybookTransaction } from '@features/daybook/hooks/useCreateDaybookTransaction'
import { CreateDaybookTransactionPayload, CreateDaybookTransactionResponseData } from '@features/daybook/query-api'
import { useApproveMatchWithPostings } from '@features/paymentReconciliationModal/hooks'
import { PostingType } from '@features/paymentReconciliationModal/types/postingType'
import { BankMatchUiFlow, UiSource } from '@features/reconciliation/enums'
import { invalidateReconciliationQueries } from '@features/reconciliation/utils'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { queryClient } from '../../../../../../../config/queryClient'
import { QueryKeys } from '../../../../../../../enums/queryKeys'
import { APIError } from '../../../../../../../utils'

interface UseCreateManualPaymentReconciliationProps {
  contraAccountId: string
  matchId: string
  onSuccess?: () => void
  onError?: (error?: APIError) => void
}

export const useCreateManualPaymentReconciliation = ({
  contraAccountId,
  matchId,
  onSuccess,
  onError,
}: UseCreateManualPaymentReconciliationProps) => {
  const { organization } = useUserOrganization()
  const { approve, isProcessing: isProcessingApproving } = useApproveMatchWithPostings({
    onSuccess: () => {
      invalidateReconciliationQueries(queryClient)
      queryClient.invalidateQueries(QueryKeys.NextVoucherNo)
      onSuccess?.()
    },
    onError: (error?: APIError) => {
      onError?.(error)
    },
    uiSource: UiSource.AccountingTransactionView,
    uiFlow: BankMatchUiFlow.Created,
  })

  const { createTransaction, isProcessing: isProcessingUpdating } = useCreateDaybookTransaction({
    onSuccess: (data: CreateDaybookTransactionResponseData) => {
      const createdPosting = data.postings.find((posting) => posting.accountId === contraAccountId)

      if (createdPosting) {
        approve({
          matchId,
          subjects: [{ id: createdPosting.id, type: PostingType.Posting }],
        })
      }
    },
    onError: (error?: APIError) => {
      onError?.(error)
    },
  })

  const createManualPaymentReconciliation = useCallback(
    (payload: CreateDaybookTransactionPayload) => {
      if (!organization || !contraAccountId || !matchId) {
        return
      }

      createTransaction(payload)
    },
    [organization, contraAccountId, matchId, createTransaction],
  )

  return {
    createManualPaymentReconciliation,
    isProcessing: isProcessingApproving || isProcessingUpdating,
  }
}
